<!--
	Shared character-limited textarea (LSP-16848) — the multi-line peer of CharacterLimitedTextField,
	with the same contract: a live code-point counter (an emoji is 1, never UTF-16 `.length`), soft
	overflow (no native maxlength and no truncation — the counter and field turn red past the limit),
	and a code-point-aware rule that blocks save through the parent form. See
	CharacterLimitedTextField.vue for the form-level save pattern.

	Everything else — v-model, label, placeholder, rows, auto-grow, v-test, events, slots — passes
	straight through to v-textarea. Two exceptions, both bound before v-bind="passthroughAttrs" below so
	a caller's own binding still wins: persistent-counter (R1), on so the live counter is never
	focus-gated; and aria-invalid (break-it round 4), reflecting the field's own live validity so a
	screen-reader user is told when the soft over-limit refusal blocks save — see useCharacterLimit's
	`ariaInvalid` doc comment.

	The #counter slot below reuses that same aria-invalid signal to color the counter text (review
	finding: Vuetify's own VCounter only reddens when ITS OWN value exceeds max, so a field invalid
	for a DIFFERENT reason — e.g. a caller's server-unit backstop rule failing while the code-point
	count is still under the limit — showed a red border and error message next to a counter that
	looked fine, which read as self-contradictory). No caller of this component supplies its own
	#counter slot today, so this never collides with the generic slot-forwarding loop below.
-->
<template>
	<v-textarea
		ref="fieldRef"
		persistent-counter
		:aria-invalid="ariaInvalid"
		:model-value="textareaValue"
		@input="handleTextareaInput"
		v-bind="compositionSafeAttrs"
		:counter="resolvedMaxLength"
		:counter-value="compositionCounterValue"
		:rules="mergedRules"
		@compositionstart="startComposition"
		@compositionend="endComposition">
		<!-- Bold the trailing (Required)/(Optional) tag on the label (LSP-16777), matching BaseTextField. -->
		<template v-if="$attrs.label && !$slots.label" #label>
			<FieldLabel :label="String($attrs.label)" />
		</template>
		<template #counter="{ counter }">
			<span :class="{ 'text-error': ariaInvalid === 'true' }">{{ counter }}</span>
		</template>
		<template v-for="(_, slot) in $slots" #[slot]="scope" :key="slot">
			<slot :name="slot" v-bind="scope || {}"></slot>
		</template>
	</v-textarea>
</template>

<script setup lang="ts">
	import { computed, ref, useAttrs, useTemplateRef, watch } from "vue";
	import type { VTextarea } from "vuetify/components";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";
	import { useCharacterLimit, type CharacterLimitProps } from "@/composables/useCharacterLimit";

	// inheritAttrs must stay false: we bind a FILTERED copy of $attrs, and Vue would otherwise
	// auto-apply the unfiltered set to the root element and put a caller's maxlength back.
	defineOptions({ inheritAttrs: false });

	const props = withDefaults(defineProps<CharacterLimitProps>(), {
		rules: () => [],
		overLimitMessage: undefined,
		trim: true
	});
	const emit = defineEmits<{ "update:modelValue": [value: string] }>();
	const fieldRef = useTemplateRef<InstanceType<typeof VTextarea>>("fieldRef");
	const { passthroughAttrs, counterValue, resolvedMaxLength, ariaInvalid, mergedRules, validate, resetValidation, isValid, focus, blur } =
		useCharacterLimit("CharacterLimitedTextArea", fieldRef, props);
	const attrs = useAttrs();
	const modelValue = computed(() => String(attrs.modelValue ?? attrs["model-value"] ?? ""));
	const isComposing = ref(false);
	const textareaValue = ref(modelValue.value ?? "");
	const compositionDraft = ref<string | null>(null);
	const compositionCounterValue = (value: unknown) => counterValue.value(compositionDraft.value ?? value);
	const compositionSafeAttrs = computed(() => {
		const attrsWithoutModel = { ...passthroughAttrs.value };
		delete attrsWithoutModel.modelValue;
		delete attrsWithoutModel["model-value"];
		delete attrsWithoutModel["onUpdate:modelValue"];
		delete attrsWithoutModel["onUpdate:model-value"];
		return attrsWithoutModel;
	});

	// macOS Dictation repeatedly replaces a provisional DOM range. Vuetify's normal controlled-model
	// write-back replaces that range with the parent's value between results, so the OS starts appending
	// the whole transcript. Freeze the rendered model while composing; keep only the counter's draft
	// current, then resume external synchronization once the OS has finalized the range. // guards LSP-17913
	watch(modelValue, (nextModelValue) => {
		if (!isComposing.value) {
			textareaValue.value = nextModelValue ?? "";
		}
	});

	function updateTextareaValue(nextValue: string) {
		if (isComposing.value) {
			compositionDraft.value = nextValue;
		} else {
			textareaValue.value = nextValue;
		}
		emit("update:modelValue", nextValue);
	}

	function handleTextareaInput(event: Event) {
		if (!(event.target instanceof HTMLTextAreaElement)) {
			return;
		}
		updateTextareaValue(event.target.value);
	}

	function startComposition() {
		isComposing.value = true;
		compositionDraft.value = textareaValue.value;
	}

	function endComposition() {
		isComposing.value = false;
		textareaValue.value = modelValue.value ?? "";
		compositionDraft.value = null;
	}

	// Ref-based validation and focus for callers outside a v-form. Vue's expose proxy does not forward
	// the inner component's own methods, so each one needed here has to be re-exposed explicitly.
	defineExpose({ validate, resetValidation, isValid, focus, blur });
</script>
